import "./topbar.js";
import "./sidebar.js";

import { contextStore } from "../auth/context-store.js";
import { resolveAppVersion } from "../../shared/branding/app-version.js";

declare const __APP_VERSION__: string;
declare const __APP_BUILD_TIME__: string;
declare const __APP_BACKEND_ENV__: string;

function decorativeDisplayVersion(): string {
  const version = typeof __APP_VERSION__ !== "undefined" && __APP_VERSION__ ? __APP_VERSION__ : null;
  const buildNumber = typeof __APP_BUILD_TIME__ !== "undefined" && __APP_BUILD_TIME__ ? __APP_BUILD_TIME__ : null;
  const environment = typeof __APP_BACKEND_ENV__ !== "undefined" && __APP_BACKEND_ENV__ ? __APP_BACKEND_ENV__ : null;
  return resolveAppVersion({ version, buildNumber, environment }).displayVersion;
}

class AppShell extends HTMLElement {
  private unsubscribe: (() => void) | null = null;

  connectedCallback() {
    this.innerHTML = `
      <!-- GLOBAL INDUSTRIAL BACKGROUND -->
      <div class="global-industrial-bg" id="global-bg">
        <!-- Layer 1: Base gradient -->
        <div class="bg-base"></div>

        <!-- Layer 2: Industrial image -->
        <div class="bg-image"></div>

        <!-- Layer 3: Directional overlays -->
        <div class="bg-overlay"></div>
        <div class="bg-vignette"></div>

        <!-- Layer 4: Engineering grid -->
        <div class="bg-grid"></div>
        <div class="bg-grid-fine"></div>

        <!-- Layer 5: Technical geometry (SVG) -->
        <svg class="bg-geometry" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
          <!-- Large partial circle — center-right area -->
          <circle cx="1100" cy="480" r="320" fill="none" stroke="rgba(255,255,255,0.025)" stroke-width="1"/>
          <circle cx="1100" cy="480" r="280" fill="none" stroke="rgba(255,255,255,0.018)" stroke-width="0.5"/>

          <!-- Smaller technical circle — upper area -->
          <circle cx="750" cy="180" r="140" fill="none" stroke="rgba(255,255,255,0.03)" stroke-width="0.5"/>

          <!-- Arc near bottom-left -->
          <path d="M 0 900 A 500 500 0 0 1 400 600" fill="none" stroke="rgba(255,255,255,0.02)" stroke-width="1"/>

          <!-- Crosshair: top-left area -->
          <line x1="280" y1="140" x2="320" y2="140" stroke="rgba(255,255,255,0.12)" stroke-width="0.5"/>
          <line x1="300" y1="120" x2="300" y2="160" stroke="rgba(255,255,255,0.12)" stroke-width="0.5"/>
          <circle cx="300" cy="140" r="8" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="0.5"/>

          <!-- Crosshair: center-right -->
          <line x1="1480" y1="300" x2="1520" y2="300" stroke="rgba(255,255,255,0.10)" stroke-width="0.5"/>
          <line x1="1500" y1="280" x2="1500" y2="320" stroke="rgba(255,255,255,0.10)" stroke-width="0.5"/>
          <circle cx="1500" cy="300" r="6" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="0.5"/>

          <!-- Crosshair: bottom-right -->
          <line x1="1680" y1="880" x2="1720" y2="880" stroke="rgba(255,255,255,0.10)" stroke-width="0.5"/>
          <line x1="1700" y1="860" x2="1700" y2="900" stroke="rgba(255,255,255,0.10)" stroke-width="0.5"/>
          <circle cx="1700" cy="880" r="6" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="0.5"/>

          <!-- Crosshair: bottom-left -->
          <line x1="180" y1="920" x2="220" y2="920" stroke="rgba(255,255,255,0.10)" stroke-width="0.5"/>
          <line x1="200" y1="900" x2="200" y2="940" stroke="rgba(255,255,255,0.10)" stroke-width="0.5"/>
          <circle cx="200" cy="920" r="6" fill="none" stroke="rgba(255,255,255,0.06)" stroke-width="0.5"/>

          <!-- Engineering reference lines -->
          <line x1="0" y1="540" x2="1920" y2="540" stroke="rgba(255,255,255,0.02)" stroke-width="0.5"/>
          <line x1="960" y1="0" x2="960" y2="1080" stroke="rgba(255,255,255,0.015)" stroke-width="0.5"/>

          <!-- Short horizontal tick marks -->
          <line x1="440" y1="108" x2="520" y2="108" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/>
          <line x1="1350" y1="750" x2="1440" y2="750" stroke="rgba(255,255,255,0.04)" stroke-width="0.5"/>
          <line x1="100" y1="650" x2="160" y2="650" stroke="rgba(255,255,255,0.04)" stroke-width="0.5"/>

          <!-- Short vertical tick marks -->
          <line x1="1600" y1="180" x2="1600" y2="240" stroke="rgba(255,255,255,0.04)" stroke-width="0.5"/>
          <line x1="350" y1="800" x2="350" y2="860" stroke="rgba(255,255,255,0.03)" stroke-width="0.5"/>
        </svg>

        <!-- Layer 6: Institutional decorative text -->
        <div class="bg-decor-text">
          <!-- Center-left: INSPEÇÃO INTEGRIDADE CONFIABILIDADE -->
          <div class="decor-block decor-cl">
            <span class="decor-label">INSPEÇÃO</span><br>
            <span class="decor-label">INTEGRIDADE</span><br>
            <span class="decor-label">CONFIABILIDADE</span>
          </div>

          <!-- Right area: PRECISÃO SEGURANÇA DESEMPENHO -->
          <div class="decor-block decor-cr">
            <span class="decor-label">PRECISÃO</span><br>
            <span class="decor-label">SEGURANÇA</span><br>
            <span class="decor-label">DESEMPENHO</span>
          </div>

          <!-- Center-lower: ANALISAR MONITORAR PLANEJAR PRESERVAR -->
          <div class="decor-block decor-cm">
            <span class="decor-label">ANALISAR</span><br>
            <span class="decor-label">MONITORAR</span><br>
            <span class="decor-label">PLANEJAR</span><br>
            <span class="decor-label">PRESERVAR</span>
          </div>

          <!-- Bottom-left: ATIVOS SEGUROS / OPERAÇÕES MAIS FORTES -->
          <div class="decor-block decor-bl">
            <span class="decor-bar-v"></span>
            <div>
              <span class="decor-label">ATIVOS SEGUROS</span><br>
              <span class="decor-label">OPERAÇÕES MAIS FORTES</span>
            </div>
          </div>

          <!-- Bottom-center: version -->
          <div class="decor-block decor-bc">
            <span class="decor-label" style=";">${decorativeDisplayVersion()}</span>
          </div>

          <!-- Bottom-right: TECNOLOGIA A SERVIÇO DA INTEGRIDADE -->
          <div class="decor-block decor-br">
            <span class="decor-bar-h"></span>
            <div>
              <span class="decor-label">TECNOLOGIA A SERVIÇO</span><br>
              <span class="decor-label">DA INTEGRIDADE</span>
            </div>
          </div>
        </div>
      </div>
      <!-- END GLOBAL BACKGROUND -->

      <app-topbar style="display: none;"></app-topbar>
      <app-sidebar style="display: none;"></app-sidebar>
      <!-- Backdrop do Drawer mobile (item: Sidebar → Drawer no celular). Só existe visualmente <768px via CSS; o clique fecha o Drawer. -->
      <div class="sidebar-backdrop" id="sidebar-backdrop"></div>
      <app-page-container id="page-root"></app-page-container>
    `;

    this.updateVisibility();
    this.unsubscribe = contextStore.subscribe(() => this.updateVisibility());

    this.querySelector("#sidebar-backdrop")?.addEventListener("click", () => {
      this.removeAttribute("mobile-nav-open");
    });

    // Global Error Boundary
    window.addEventListener("error", this.handleError.bind(this));
    window.addEventListener("unhandledrejection", this.handlePromiseRejection.bind(this));
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    window.removeEventListener("error", this.handleError.bind(this));
    window.removeEventListener("unhandledrejection", this.handlePromiseRejection.bind(this));
  }

  private updateVisibility() {
    const ctx = contextStore.get();
    const topbar = this.querySelector("app-topbar") as HTMLElement;
    const sidebar = this.querySelector("app-sidebar") as HTMLElement;
    const pageContainer = this.querySelector("app-page-container") as HTMLElement;
    const globalBg = this.querySelector("#global-bg") as HTMLElement;
    
    if (topbar) topbar.style.display = ctx.isAuthenticated ? "flex" : "none";
    if (sidebar) sidebar.style.display = ctx.isAuthenticated ? "flex" : "none";
    if (globalBg) globalBg.style.display = ctx.isAuthenticated ? "block" : "none"; // Hide global BG on login, since login has its own

    if (!ctx.isAuthenticated) {
      this.style.display = "block";
      this.style.width = "100vw";
      this.style.height = "100vh";
      this.style.overflow = "hidden";
      if (pageContainer) {
        pageContainer.style.padding = "0";
        pageContainer.style.margin = "0";
        pageContainer.style.width = "100vw";
        pageContainer.style.height = "100vh";
        pageContainer.style.overflow = "hidden";
      }
    } else {
      this.style.display = "";
      this.style.width = "";
      this.style.height = "";
      this.style.overflow = "";
      if (pageContainer) {
        pageContainer.style.padding = "";
        pageContainer.style.margin = "";
        pageContainer.style.width = "";
        pageContainer.style.height = "";
        pageContainer.style.overflow = "";
      }
    }
  }

  private handleError(event: ErrorEvent) {
    this.showErrorScreen(event.message, event.error?.stack || "No stack trace available.");
  }

  private handlePromiseRejection(event: PromiseRejectionEvent) {
    const reason = event.reason;
    const msg = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : "No stack trace available.";
    this.showErrorScreen(msg, stack || "No stack trace available.");
  }

  private showErrorScreen(message: string, stack: string) {
    const root = this.querySelector("#page-root");
    if (root) {
      root.innerHTML = `
        <div style="padding: 48px 32px; max-width: 720px; margin: 0 auto; position: relative; z-index: 10;">
          <h1 style="color: var(--status-danger-text); margin-bottom: 12px; font-size: 1.375rem; font-weight: 700;">
            Erro Crítico da Aplicação
          </h1>
          <p style="margin-bottom: 20px; font-size: 0.9375rem; color: var(--color-text-secondary); line-height: 1.6;">
            Ocorreu um erro inesperado. O estado da aplicação pode estar corrompido.
          </p>
          <div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); padding: 16px; border-radius: var(--radius-sm); font-family: var(--font-mono); font-size: 0.8125rem; box-shadow: var(--shadow-sm); backdrop-filter: blur(4px);">
            <strong style="color: var(--status-danger-text);">${message}</strong>
            <pre style="margin-top: 10px; font-size: 0.75rem; overflow-x: auto; color: var(--status-danger-text); ; white-space: pre-wrap; word-break: break-all;">${stack}</pre>
          </div>
          <button onclick="window.location.reload()" style="margin-top: 20px; padding: 8px 20px; background: var(--color-surface-secondary); border: 1px solid var(--color-border-default); color: var(--color-text-primary); border-radius: var(--radius-sm); cursor: pointer; font-size: 0.875rem; font-weight: 600; font-family: var(--font-sans); transition: background var(--transition-fast);" onmouseover="this.style.background='var(--color-surface-primary)'" onmouseout="this.style.background='var(--color-surface-secondary)'">
            Recarregar Aplicação
          </button>
        </div>
      `;
    }
  }
}

if (!customElements.get("app-shell")) {
  customElements.define("app-shell", AppShell);
}
